(function () {
  'use strict';

  if (customElements.get('sidebar-promo-carousel')) return;

  class SidebarPromoCarousel extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.timer = null;
      this.manuallyPaused = false;
      this.interactionPaused = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleAutoplay = this.handleAutoplay.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
      this.handleDrawerClose = this.handleDrawerClose.bind(this);
    }

    connectedCallback() {
      if (this.dataset.promoBound === 'true') return;
      this.dataset.promoBound = 'true';
      this.slides = Array.from(this.querySelectorAll('[data-promo-slide]'));
      this.track = this.querySelector('[data-promo-track]');
      this.controls = this.querySelector('[data-promo-controls]');
      this.previousButton = this.querySelector('[data-promo-previous]');
      this.nextButton = this.querySelector('[data-promo-next]');
      this.autoplayButton = this.querySelector('[data-promo-autoplay]');
      this.status = this.querySelector('[data-promo-status]');
      this.drawer = this.closest('[data-desktop-sidebar]');
      this.videos = Array.from(this.querySelectorAll('video'));
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.interval = Math.max(5000, Number(this.dataset.interval) || 6000);

      if (!this.slides.length) {
        this.hidden = true;
        return;
      }

      const hasMultipleSlides = this.slides.length > 1;
      this.classList.toggle('sidebar-promo--multiple', hasMultipleSlides);
      if (this.controls) this.controls.hidden = !hasMultipleSlides;

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.autoplayButton?.addEventListener('click', this.handleAutoplay);
      this.addEventListener('pointerenter', this.handlePointerEnter);
      this.addEventListener('pointerleave', this.handlePointerLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.reducedMotion.addEventListener('change', this.handleMotionChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('theme:drawer:open', this.handleDrawerOpen);
      document.addEventListener('theme:drawer:close', this.handleDrawerClose);

      this.classList.add('is-ready');
      this.showSlide(0, false);
      this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      this.pauseVideos();
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.autoplayButton?.removeEventListener('click', this.handleAutoplay);
      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.reducedMotion.removeEventListener('change', this.handleMotionChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('theme:drawer:open', this.handleDrawerOpen);
      document.removeEventListener('theme:drawer:close', this.handleDrawerClose);
      this.removeAttribute('data-promo-bound');
    }

    showSlide(index, announce) {
      if (!this.slides.length) return;
      this.activeIndex = (index + this.slides.length) % this.slides.length;
      if (this.track) {
        this.track.style.setProperty('--sidebar-promo-index', String(this.activeIndex));
      }

      for (const [slideIndex, slide] of this.slides.entries()) {
        const active = slideIndex === this.activeIndex;
        slide.hidden = false;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) {
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('inert', '');
        }
      }

      this.syncActiveVideo();

      if (announce && this.status) {
        this.status.textContent = '';
        window.requestAnimationFrame(() => {
          this.status.textContent = this.slides[this.activeIndex].dataset.slideLabel || '';
        });
      }
    }

    pauseVideos() {
      for (const video of this.videos || []) {
        if (!video.paused) video.pause();
      }
    }

    syncActiveVideo() {
      const activeSlide = this.slides[this.activeIndex];
      const drawerIsOpen = this.drawer?.getAttribute('aria-hidden') === 'false';

      for (const video of this.videos || []) {
        const isActiveVideo = activeSlide?.contains(video);
        if (!isActiveVideo || !drawerIsOpen || document.hidden || this.reducedMotion.matches) {
          if (!video.paused) video.pause();
          continue;
        }

        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }
    }

    handlePrevious() {
      this.showSlide(this.activeIndex - 1, true);
      this.restartTimer();
    }

    handleNext() {
      this.showSlide(this.activeIndex + 1, true);
      this.restartTimer();
    }

    handleAutoplay() {
      this.manuallyPaused = !this.manuallyPaused;
      this.autoplayButton.setAttribute('aria-pressed', String(this.manuallyPaused));
      this.autoplayButton.setAttribute(
        'aria-label',
        this.manuallyPaused ? this.autoplayButton.dataset.labelPlay : this.autoplayButton.dataset.labelPause
      );
      this.autoplayButton.classList.toggle('is-paused', this.manuallyPaused);
      this.restartTimer();
    }

    handlePointerEnter() {
      this.interactionPaused = true;
      this.stopTimer();
    }

    handlePointerLeave() {
      this.interactionPaused = false;
      this.startTimer();
    }

    handleFocusIn() {
      this.interactionPaused = true;
      this.stopTimer();
    }

    handleFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.interactionPaused = false;
      this.startTimer();
    }

    handleMotionChange() {
      this.syncActiveVideo();
      this.restartTimer();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseVideos();
      } else {
        this.syncActiveVideo();
      }
      this.restartTimer();
    }

    handleDrawerOpen(event) {
      if (event.detail?.drawer !== this.drawer) return;
      this.syncActiveVideo();
      this.startTimer();
    }

    handleDrawerClose(event) {
      if (event.detail?.drawer !== this.drawer) return;
      this.stopTimer();
      this.pauseVideos();
    }

    canAutoplay() {
      return this.autoplayEnabled
        && !this.manuallyPaused
        && !this.interactionPaused
        && !this.reducedMotion.matches
        && !document.hidden
        && this.drawer?.getAttribute('aria-hidden') === 'false';
    }

    startTimer() {
      if (!this.canAutoplay() || this.timer) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.showSlide(this.activeIndex + 1, false);
        this.startTimer();
      }, this.interval);
    }

    stopTimer() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    restartTimer() {
      this.stopTimer();
      this.startTimer();
    }
  }

  customElements.define('sidebar-promo-carousel', SidebarPromoCarousel);
})();
